import React from "react";
import { Link } from "react-router-dom";
import { FiArrowRight } from "react-icons/fi";
import type { LanguageCoverage } from "../../hooks/useDashboardExtras";
import { PanelCard } from "./dashboardShared";

const barTone = (percent: number): string => (percent >= 80 ? "bg-emerald-500" : percent >= 40 ? "bg-amber-500" : "bg-rose-500");

const Bar: React.FC<{ row: LanguageCoverage }> = ({ row }) => (
	<li className="flex items-center gap-3 px-5 py-2.5">
		<span className={`fi fi-${row.flag} h-3.5 w-5 shrink-0 rounded-sm ring-1 ring-slate-200`} aria-hidden />
		<span className="w-24 shrink-0 truncate text-sm font-semibold text-slate-700">{row.label}</span>
		<div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
			<div className={`h-2 rounded-full ${barTone(row.percent)}`} style={{ width: `${row.percent}%` }} />
		</div>
		<span className="w-12 shrink-0 text-right text-sm font-bold tabular-nums text-slate-900">{row.percent}%</span>
		<span className="w-14 shrink-0 text-right text-[11px] tabular-nums text-slate-400">
			{row.translated}/{row.total}
		</span>
	</li>
);

export interface TranslationCoverageProps {
	data: LanguageCoverage[];
	loading: boolean;
}

/** Share of the French source content translated into each language. Live snapshot (no stored history). */
export const TranslationCoverage: React.FC<TranslationCoverageProps> = ({ data, loading }) => (
	<PanelCard
		title="Couverture des traductions"
		subtitle="Part du contenu FR traduit, par langue"
		bodyClassName="py-1"
		action={
			<Link to="/traduction" className="inline-flex items-center gap-1 text-[11px] font-semibold text-sky-600 hover:text-sky-700">
				Gérer <FiArrowRight className="h-3 w-3" />
			</Link>
		}
	>
		{loading ? (
			<ul className="divide-y divide-slate-100">
				{Array.from({ length: 6 }).map((_, i) => (
					<li key={`tc-skel-${i}`} className="flex items-center gap-3 px-5 py-2.5">
						<div className="h-3.5 w-5 animate-pulse rounded bg-slate-100" />
						<div className="h-3 w-20 animate-pulse rounded bg-slate-100" />
						<div className="h-2 flex-1 animate-pulse rounded-full bg-slate-100" />
						<div className="h-3 w-10 animate-pulse rounded bg-slate-100" />
					</li>
				))}
			</ul>
		) : data.length === 0 ? (
			<div className="px-5 py-10 text-center text-sm text-slate-500">Couverture indisponible.</div>
		) : (
			<ul className="divide-y divide-slate-100">
				{data.map((row) => (
					<Bar key={row.code} row={row} />
				))}
			</ul>
		)}
	</PanelCard>
);

export default TranslationCoverage;
